import { useIsExecutingMutation } from "@wojo/apollo";
import { useI18n } from "@wojo/localization";
import {
    Button,
    FormCombobox,
    FormRadioGroup,
    RadioButtonGroupItem,
    Text,
} from "@wojo/ui";
import { useQueryState } from "nuqs";
import React, { useCallback, useEffect, useId, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
    DomainError,
    useTripBuilderUpdateFlightPackagesMutation,
    ValidationError,
} from "../../../../generated/graphql";
import { getCurrency } from "../../../../hooks";
import { parseAsEncodedString } from "../../search-params";
import { useTripBuilderQuote } from "../context/TripBuilderQuoteStepContext";
import {
    LAND_ONLY_GATEWAY_CODE,
    useAvailableFlightPackages,
} from "../hooks/use-available-flight-packages";
import { useTripBuilderAnalytics } from "../hooks/use-trip-builder-analytics";
import { QuoteFlightsModal } from "./QuoteFlightsModal";
import styles from "./TripBuilderQuoteStep.module.scss";
import { FormValues } from "../context/TripBuilderQuoteForm";
import currency from "currency.js";
import { PriceComparison } from "../../../../price-comparison";
import { useFlightPrices } from "../hooks";
import { Ga4PricedItem } from "@wojo/analytics";
import { useTripsWithOffers } from "../hooks/use-trips-with-offers";

type QuoteFlightPickerProps = {
    /**
     * Automatically set the closest gateway based on geolocation to the quote.
     * If false, the gateway may still be retrieved, but will not be set.
     * @default true
     */
    autoSelectGeolocation?: boolean;
};

export const QuoteFlightPicker: React.FC<QuoteFlightPickerProps> = ({
    autoSelectGeolocation = true,
}) => {
    const { draftQuote } = useTripBuilderQuote();
    const { addToCart, removeFromCart } = useTripBuilderAnalytics();
    const { formatCurrency } = getCurrency();
    const { productPage } = useI18n("productPage");
    const {
        airportGatewayOptions,
        isLandOnlyAvailable,
        availableFlightPackages,
        closestRoundtripFlightSummary,
    } = useAvailableFlightPackages();
    const isExecutingMutation = useIsExecutingMutation([
        "TripBuilderUpdateFlightPackages",
    ]);
    const [queryGatewayDisplayName, setQueryGatewayDisplayName] = useQueryState(
        "priceWithFlightsGateway",
        parseAsEncodedString,
    );
    // TODO: Bring back when we have flights in algolia again, need to think of a more graceful way
    // to handle this bug if we lose flights in algolia again: https://ef-wojo.atlassian.net/browse/WWR-839
    // const [_, setQueryWithFlights] = useQueryState(
    //     "priceWithFlights",
    //     parseAsBoolean,
    // );
    const queryGateway = airportGatewayOptions.find(
        (gateway) => gateway.label === queryGatewayDisplayName,
    );
    const flightOption = useWatch<FormValues, "flightOption">({
        name: "flightOption",
    });
    const mostRecentGateway = useWatch<FormValues, "mostRecentGateway">({
        name: "mostRecentGateway",
    });
    const { setValue, trigger } = useFormContext<FormValues>();
    const [updateFlights, flightResults] =
        useTripBuilderUpdateFlightPackagesMutation();
    const mostRecentlySelectedFlightPackage = availableFlightPackages?.find(
        (packages) => packages.homeGateway.code === mostRecentGateway,
    );
    const displaySelectedAirport =
        mostRecentlySelectedFlightPackage &&
        mostRecentlySelectedFlightPackage.homeGateway.code !==
            LAND_ONLY_GATEWAY_CODE;
    const [error, setError] = useState<string | undefined>(undefined);
    const labelId = useId();
    const [showAirportSelector, setShowAirportSelector] = useState(
        !mostRecentGateway,
    );
    const { getRoundtripSalePrice } = useFlightPrices();
    const roundtripSalePrice = getRoundtripSalePrice(
        mostRecentlySelectedFlightPackage,
    );
    const tripsWithFlightOffers = useTripsWithOffers();

    const handleAirportChange = useCallback(
        async (
            props:
                | {
                      option: "yesFlights";
                      gateway: string;
                      onSuccess?: () => void;
                  }
                | {
                      option: "noFlights";
                      onSuccess?: () => void;
                  },
        ) => {
            if (draftQuote?._id && !flightResults.loading) {
                const homeGatewayCode =
                    props.option === "yesFlights" && props.gateway
                        ? props.gateway
                        : LAND_ONLY_GATEWAY_CODE;

                const packagesToChangeTo = availableFlightPackages?.find(
                    (packages) => packages.homeGateway.code === homeGatewayCode,
                );
                const departingLineItem =
                    packagesToChangeTo?.departingFlight.flight.lineItemCode;
                const returningLineItem =
                    packagesToChangeTo?.returningFlight.flight.lineItemCode;

                if (departingLineItem && returningLineItem) {
                    await updateFlights({
                        variables: {
                            id: draftQuote._id,
                            inputs: draftQuote.order.trips.map((trip) => ({
                                departing: {
                                    lineItemCode: departingLineItem,
                                },
                                returning: {
                                    lineItemCode: returningLineItem,
                                },
                                tripId: trip._id,
                            })),
                        },
                        onCompleted: (data) => {
                            if (
                                data.updateDraftOrderFlightPackages
                                    .__typename !== "DraftOrder"
                            ) {
                                setError(
                                    (
                                        data.updateDraftOrderFlightPackages as
                                            | DomainError
                                            | ValidationError
                                    ).message,
                                );
                            } else {
                                setError(undefined);
                                props.onSuccess?.();
                            }
                        },
                    });
                } else {
                    setError(
                        productPage.tripBuilder.quote.flights.errorMissingPackage.l(),
                    );
                }

                const reducedTrips = tripsWithFlightOffers.reduce(
                    (acc, trip) => {
                        const roundTripPrice = getRoundtripSalePrice(
                            packagesToChangeTo,
                            trip._id,
                        );
                        acc.items.push({
                            item_id: departingLineItem ?? "",
                            item_name: "Round Trip",
                            item_variant: "Flight",
                            price: roundTripPrice,
                            discount: currency(
                                packagesToChangeTo?.price ?? 0,
                            ).subtract(roundTripPrice).value,
                            quantity: 1,
                        });
                        acc.totalPrice.add(roundTripPrice);
                        return acc;
                    },
                    {
                        items: new Array<
                            Ga4PricedItem & {
                                price: number;
                                discount: number;
                                quantity: number;
                            }
                        >(),
                        totalPrice: currency(0),
                    },
                );

                if (homeGatewayCode === LAND_ONLY_GATEWAY_CODE) {
                    removeFromCart(
                        reducedTrips.totalPrice.value,
                        reducedTrips.items,
                        {
                            ga4_object: "TRIP_BUILDER",
                            ga4_action: "FLIGHT_SELECTED",
                            flightsOption: "flights",
                            eventLabel: LAND_ONLY_GATEWAY_CODE,
                        },
                    );
                } else {
                    addToCart(
                        reducedTrips.totalPrice.value,
                        reducedTrips.items,
                        {
                            ga4_object: "TRIP_BUILDER",
                            ga4_action: "FLIGHT_SELECTED",
                            flightsOption: "flights",
                            eventLabel: homeGatewayCode || "yesFlights",
                        },
                    );
                }
            }
        },
        [
            setError,
            draftQuote,
            updateFlights,
            availableFlightPackages,
            productPage,
            flightResults.loading,
            addToCart,
            removeFromCart,
            getRoundtripSalePrice,
            tripsWithFlightOffers,
        ],
    );

    useEffect(() => {
        const shouldInitializeWithClosestGateway =
            closestRoundtripFlightSummary?.code && !mostRecentGateway;
        if (!shouldInitializeWithClosestGateway) {
            return;
        }
        if (autoSelectGeolocation) {
            (async () => {
                await handleAirportChange({
                    option: "yesFlights",
                    gateway:
                        queryGateway?.value ??
                        closestRoundtripFlightSummary.code,
                    onSuccess: async () => {
                        setValue(
                            "mostRecentGateway",
                            queryGateway?.value ??
                                closestRoundtripFlightSummary.code,
                        );
                        await setQueryGatewayDisplayName(
                            queryGateway?.label ??
                                closestRoundtripFlightSummary.displayName,
                        );
                        setShowAirportSelector(false);
                    },
                });
            })();
        } else {
            // Defaults the gateway selector to the closest one if there isn't already one set
            setValue(
                "mostRecentGateway",
                queryGateway?.value ?? closestRoundtripFlightSummary.code,
            );
            setShowAirportSelector(false);
        }
    }, [
        closestRoundtripFlightSummary?.code,
        mostRecentGateway,
        handleAirportChange,
        autoSelectGeolocation,
        setShowAirportSelector,
        setValue,
        setQueryGatewayDisplayName,
        closestRoundtripFlightSummary?.displayName,
        queryGateway?.value,
        queryGateway?.label,
    ]);

    useEffect(() => {
        trigger("flightOption");
    }, [error, trigger]);

    const flightsOptionLabel = mostRecentlySelectedFlightPackage ? (
        productPage.tripBuilder.quote.flights.withFlightsSelectedLabel.l({
            cityName:
                mostRecentlySelectedFlightPackage.homeGateway.internalCityName,
            cityCode:
                mostRecentlySelectedFlightPackage.homeGateway.stateCode ?? "",
        })
    ) : (
        <span id={labelId}>
            {productPage.tripBuilder.quote.flights.withFlightsUnselectedLabel.l()}
        </span>
    );

    const flightsOptionDetailLabel = displaySelectedAirport
        ? formatCurrency(mostRecentlySelectedFlightPackage.price)
        : undefined;

    const flightsOptionChangeGateway = !showAirportSelector ? (
        <Button
            variant="quiet"
            type="button"
            onClick={() => setShowAirportSelector(true)}
        >
            {productPage.tripBuilder.quote.flights.changeAirportLabel.l()}
        </Button>
    ) : (
        <FormCombobox
            name="mostRecentGateway"
            aria-labelledby={labelId}
            data-testid="airport-selector"
            hideLabel
            loading={flightResults.loading}
            disabled={flightResults.loading}
            label={productPage.tripBuilder.quote.flights.withFlightsUnselectedLabel.l()}
            placeholder={productPage.tripBuilder.quote.flights.airportInputPlaceholder.l()}
            onChange={async (value) => {
                if (typeof value === "string") {
                    setValue("flightOption", "yesFlights");
                    await handleAirportChange({
                        option: "yesFlights",
                        gateway: value,
                        onSuccess: async () => {
                            setValue("mostRecentGateway", value);
                            await setQueryGatewayDisplayName(
                                airportGatewayOptions.find(
                                    (gateway) => gateway.value === value,
                                )?.label ?? null,
                            );
                            setShowAirportSelector(false);
                        },
                    });
                }
            }}
            validation={{
                required: {
                    value: flightOption === "yesFlights",
                    message:
                        productPage.tripBuilder.quote.flights.errorRequiredAirport.l(),
                },
            }}
            items={airportGatewayOptions}
        />
    );

    const items = Array<RadioButtonGroupItem>({
        label: flightsOptionLabel,
        labelDetail:
            flightsOptionDetailLabel && flightOption === "noFlights" ? (
                <PriceComparison
                    fullPrice={flightsOptionDetailLabel}
                    salePrice={formatCurrency(roundtripSalePrice)}
                />
            ) : (
                flightsOptionDetailLabel
            ),
        moreDetails: flightsOptionChangeGateway,
        value: "yesFlights",
    });

    if (isLandOnlyAvailable) {
        items.push({
            label: productPage.tripBuilder.quote.flights.landOnlyLabel.l(),
            value: "noFlights",
        });
    }

    return (
        <div className={styles["form-field"]}>
            <FormRadioGroup
                data-testid="flight-picker-radio-group"
                title={
                    <Text.Body variant="2" emphasis="2" tag="span">
                        {productPage.tripBuilder.quote.flights.heading.l()}
                    </Text.Body>
                }
                disabled={isExecutingMutation}
                onChange={async (e) => {
                    if (e.target.value === "noFlights") {
                        await handleAirportChange({
                            option: "noFlights",
                            // TODO: bring back with the other TODO above
                            // onSuccess: () => setQueryWithFlights(null),
                        });
                    } else {
                        if (mostRecentGateway) {
                            await handleAirportChange({
                                option: "yesFlights",
                                gateway: mostRecentGateway,
                                // TODO: bring back with the other TODO above
                                // onSuccess: () => setQueryWithFlights(true),
                            });
                        }
                    }
                }}
                description={<QuoteFlightsModal />}
                validation={{
                    required:
                        productPage.tripBuilder.quote.flights.errorRequired.l(),
                    validate: () => error,
                }}
                name="flightOption"
                detailPosition="right"
                items={items}
            />
        </div>
    );
};
